import React, { useState, useEffect } from 'react';
import { X, Send, User, Shield, Clock, MessageSquare } from 'lucide-react';
import { commissionMessagesService } from '../../services';

const AdminCommissionMessagingModal = ({ commission, onClose, onMessageSent, prefilledMessage }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (commission) {
      loadMessages();
      // Set pre-filled message if provided
      if (prefilledMessage) {
        setNewMessage(prefilledMessage);
      }
    }
  }, [commission, prefilledMessage]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await commissionMessagesService.getMessages(commission.id);
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Error loading messages:', error);
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      setSending(true);
      setError('');
      
      await commissionMessagesService.sendAdminMessage(
        commission.id,
        newMessage.trim()
      );

      setNewMessage('');
      await loadMessages();
      
      // Notify parent component that a message was sent
      if (onMessageSent) {
        onMessageSent();
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 168) { // 7 days
      return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
  };

  if (!commission) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Commission Messages</h2>
            <p className="text-sm text-gray-600 mt-1">
              Reservation #{commission.reservation_number} - {commission.first_name} {commission.last_name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Commission Summary */}
        <div className="p-4 bg-gray-50 border-b">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Guest:</span>
              <span className="ml-2 text-gray-900">
                {commission.guest_first_name} {commission.guest_last_name}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Agent Commission:</span>
              <span className="ml-2 text-gray-900">
                ${parseFloat(commission.agent_commission || 0).toFixed(2)}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Supplier:</span>
              <span className="ml-2 text-gray-900">{commission.supplier}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Status:</span>
              <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                commission.status === 'needs_reply' || commission.status === 'needs_admin_reply'
                  ? 'bg-yellow-100 text-yellow-800' 
                  : commission.status === 'needs_agent_reply'
                  ? 'bg-blue-100 text-blue-800'
                  : commission.status === 'pending'
                  ? 'bg-yellow-100 text-yellow-800'
                  : commission.status === 'approved'
                  ? 'bg-green-100 text-green-800'
                  : commission.status === 'paid'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {commission.status === 'needs_admin_reply' ? 'Needs Admin Reply' :
                 commission.status === 'needs_agent_reply' ? 'Needs Agent Reply' :
                 commission.status === 'needs_reply' ? 'Needs Reply' :
                 commission.status}
              </span>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="loading-spinner"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600">{error}</p>
              <button
                onClick={loadMessages}
                className="mt-2 text-blue-600 hover:text-blue-800 text-sm"
              >
                Try again
              </button>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No messages yet</p>
              <p className="text-sm text-gray-400 mt-1">Start the conversation by sending a message below</p>
            </div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                    message.sender_type === 'admin'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-900'
                  }`}
                >
                  <div className="flex items-center mb-1">
                    {message.sender_type === 'admin' ? (
                      <Shield className="w-4 h-4 mr-2" />
                    ) : (
                      <User className="w-4 h-4 mr-2" />
                    )}
                    <span className="text-xs font-medium">
                      {message.sender_type === 'admin' ? 'Admin' : 'Agent'}
                    </span>
                    <span className="text-xs ml-2 opacity-75">
                      {formatDate(message.created_at)}
                    </span>
                  </div>
                  <p className="text-sm">{message.message}</p>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Message Input */}
        <div className="p-4 border-t">
          <form onSubmit={handleSendMessage} className="flex space-x-3">
            <div className="flex-1">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message to the agent..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                disabled={sending}
              />
            </div>
            <button
              type="submit"
              disabled={!newMessage.trim() || sending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {sending ? (
                <Clock className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </form>
          {error && (
            <p className="text-red-600 text-sm mt-2">{error}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCommissionMessagingModal;
